import {CheckCircle2, AlertCircle, AlertTriangle} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import type {ValidationIssue} from './types';

type ValidationStepProps = {
  totalRows: number;
  rawRowCount: number;
  validationErrors: ValidationIssue[];
  validationWarnings: ValidationIssue[];
  csvHeaders: string[];
  onBack: () => void;
  onContinue: () => void;
};

export function ValidationStep({
  totalRows,
  rawRowCount,
  validationErrors,
  validationWarnings,
  csvHeaders,
  onBack,
  onContinue,
}: ValidationStepProps) {
  return (
    <Card className='border-0 shadow-lg bg-white overflow-hidden'>
      <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>
          Step 2: Validation
        </CardTitle>
      </CardHeader>
      <CardContent className='p-6'>
        <div className='grid grid-cols-3 gap-4 mb-6'>
          <div className='p-4 rounded-lg bg-gray-50 text-center'>
            <p className='text-2xl font-bold text-brand-navy'>{totalRows}</p>
            <p className='text-xs text-gray-500'>
              Data Rows
              {rawRowCount !== totalRows && ` (of ${rawRowCount})`}
            </p>
          </div>
          <div className='p-4 rounded-lg bg-gray-50 text-center'>
            <p className='text-2xl font-bold text-red-600'>
              {validationErrors.length}
            </p>
            <p className='text-xs text-gray-500'>Errors</p>
          </div>
          <div className='p-4 rounded-lg bg-gray-50 text-center'>
            <p className='text-2xl font-bold text-amber-600'>
              {validationWarnings.length}
            </p>
            <p className='text-xs text-gray-500'>Warnings</p>
          </div>
        </div>

        <div className='mb-4'>
          <p className='text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2'>
            Detected Columns ({csvHeaders.length})
          </p>
          <div className='flex flex-wrap gap-1.5'>
            {csvHeaders.map((h) => (
              <Badge
                key={h}
                className='bg-gray-100 text-gray-700 border-gray-200 font-mono text-xs'
              >
                {h}
              </Badge>
            ))}
          </div>
        </div>

        {validationErrors.length === 0 &&
          validationWarnings.length === 0 && (
          <div className='flex items-center gap-2 p-3 rounded-lg bg-emerald-50'>
            <CheckCircle2 className='h-5 w-5 text-emerald-600' />
            <span className='text-sm text-emerald-700 font-medium'>
              File validation passed. No issues found.
            </span>
          </div>
        )}

        {validationWarnings.length > 0 && (
          <div className='space-y-2 mb-4'>
            {validationWarnings.map((w, i) => (
              <div
                key={i}
                className='flex items-start gap-2 p-2 rounded-lg bg-amber-50 text-sm'
              >
                <AlertTriangle className='h-4 w-4 text-amber-600 mt-0.5 shrink-0' />
                <span className='text-amber-800'>
                  Row {w.row}: {w.message}
                </span>
              </div>
            ))}
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className='space-y-2'>
            {validationErrors.map((e, i) => (
              <div
                key={i}
                className='flex items-start gap-2 p-2 rounded-lg bg-red-50 text-sm'
              >
                <AlertCircle className='h-4 w-4 text-red-600 mt-0.5 shrink-0' />
                <span className='text-red-800'>
                  Row {e.row}: {e.message}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className='flex items-center justify-between mt-6 pt-4 border-t border-gray-100'>
          <Button variant='outline' onClick={onBack}>
            Back
          </Button>
          <Button onClick={onContinue}>Continue</Button>
        </div>
      </CardContent>
    </Card>
  );
}
